import { z } from 'zod';
import type { CategoryToken } from '@sleed/bizcommerce-api';

/**
 * Product-category edit form. Mirrors `CategoryToken` and the legacy admin TreeList popup
 * (Core/client/admin/src/pages/Catalog/Categories/index.js): nm required ≤100, pst (ProductSortType
 * enum) + si required, the long list of boolean flags, SEO (h1/mt/mk/md), 4 HTML info texts
 * (itxt1..4), 5 image slots (img1..5), dynamic-filter arrays + customer groups, BuyGuideId (FK) / gtax (Google taxonomy id ≤50) /
 * static_page_url (≤2000, overrides the storefront category link). `nm` and the HTML
 * texts are multilanguage server-side (writes go to the active Accept-Language, default `el`). Image
 * bytes are uploaded out-of-band (imgNFile → binaryRepoImagesApi.uploadProductCategory, needs a saved id).
 */
const nullableStr = z.string().nullable();
const nullableNum = z.number().nullable();
const idArray = z.array(z.number());

const imageSlot = {
	img: nullableStr,
	imgdel: z.boolean(),
	imgFile: z.instanceof(File).nullable()
};

export const categorySchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	cd: z.string().trim().max(50, 'validation.tooLong').nullable(),
	pst: z.number({ message: 'validation.required' }).int(),
	dsc: nullableNum,
	eurl: z.string().trim().max(400, 'validation.tooLong').nullable(),
	erpcd: nullableStr,
	crf: z.boolean(),
	cri: nullableNum,
	pid: nullableNum,
	FilterTemplateId: nullableNum,
	categoryWizardId: nullableNum,
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	wt: z.boolean(),
	szq: z.boolean(),
	ilp: z.boolean(),
	hidden_sbc: z.boolean(),
	exclude_rel_category: z.boolean(),
	itp: z.boolean(),
	disable_indexation: z.boolean(),
	disable_follow: z.boolean(),
	canonical_url: nullableStr,
	static_page_url: z.string().trim().max(2000, 'validation.tooLong').nullable(),
	gtax: z.string().trim().max(50, 'validation.tooLong').nullable(),
	BuyGuideId: nullableNum,
	frontExclusion: z.boolean(),
	isHighlighted: z.boolean(),
	chex: nullableStr,
	h1: nullableStr,
	mt: nullableStr,
	mk: nullableStr,
	md: nullableStr,
	itxt1: nullableStr,
	itxt2: nullableStr,
	itxt3: nullableStr,
	itxt4: nullableStr,
	IsDynamic: z.boolean(),
	ProductFilterIds: idArray,
	ExcludedProductFilterIds: idArray,
	CustomerGroupIds: idArray,
	img1: imageSlot.img,
	imgdel1: imageSlot.imgdel,
	img1File: imageSlot.imgFile,
	img2: imageSlot.img,
	imgdel2: imageSlot.imgdel,
	img2File: imageSlot.imgFile,
	img3: imageSlot.img,
	imgdel3: imageSlot.imgdel,
	img3File: imageSlot.imgFile,
	img4: imageSlot.img,
	imgdel4: imageSlot.imgdel,
	img4File: imageSlot.imgFile,
	img5: imageSlot.img,
	imgdel5: imageSlot.imgdel,
	img5File: imageSlot.imgFile
});

export type CategoryFormValues = z.infer<typeof categorySchema>;

export const EMPTY_CATEGORY: CategoryFormValues = {
	nm: '',
	cd: null,
	pst: 0,
	dsc: null,
	eurl: null,
	erpcd: null,
	crf: false,
	cri: null,
	pid: null,
	FilterTemplateId: null,
	categoryWizardId: null,
	iv: true,
	si: 0,
	wt: false,
	szq: false,
	ilp: false,
	hidden_sbc: false,
	exclude_rel_category: false,
	itp: false,
	disable_indexation: false,
	disable_follow: false,
	canonical_url: null,
	static_page_url: null,
	gtax: null,
	BuyGuideId: null,
	frontExclusion: false,
	isHighlighted: false,
	chex: null,
	h1: null,
	mt: null,
	mk: null,
	md: null,
	itxt1: null,
	itxt2: null,
	itxt3: null,
	itxt4: null,
	IsDynamic: false,
	ProductFilterIds: [],
	ExcludedProductFilterIds: [],
	CustomerGroupIds: [],
	img1: null,
	imgdel1: false,
	img1File: null,
	img2: null,
	imgdel2: false,
	img2File: null,
	img3: null,
	imgdel3: false,
	img3File: null,
	img4: null,
	imgdel4: false,
	img4File: null,
	img5: null,
	imgdel5: false,
	img5File: null
};

export function tokenToForm(token: CategoryToken): CategoryFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		cd: token.cd ?? null,
		pst: typeof token.pst === 'number' ? token.pst : 0,
		dsc: token.dsc ?? null,
		eurl: token.eurl ?? null,
		erpcd: token.erpcd ?? null,
		crf: token.crf ?? false,
		cri: token.cri ?? null,
		pid: token.pid ?? null,
		FilterTemplateId: token.FilterTemplateId ?? null,
		categoryWizardId: token.categoryWizardId ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		wt: token.wt ?? false,
		szq: token.szq ?? false,
		ilp: token.ilp ?? false,
		hidden_sbc: token.hidden_sbc ?? false,
		exclude_rel_category: token.exclude_rel_category ?? false,
		itp: token.itp ?? false,
		disable_indexation: token.disable_indexation ?? false,
		disable_follow: token.disable_follow ?? false,
		canonical_url: token.canonical_url ?? null,
		static_page_url: token.static_page_url ?? null,
		gtax: token.gtax ?? null,
		BuyGuideId: token.BuyGuideId ?? null,
		frontExclusion: token.frontExclusion ?? false,
		isHighlighted: token.isHighlighted ?? false,
		chex: token.chex ?? null,
		h1: token.h1 ?? null,
		mt: token.mt ?? null,
		mk: token.mk ?? null,
		md: token.md ?? null,
		itxt1: token.itxt1 ?? null,
		itxt2: token.itxt2 ?? null,
		itxt3: token.itxt3 ?? null,
		itxt4: token.itxt4 ?? null,
		IsDynamic: token.IsDynamic ?? false,
		ProductFilterIds: token.ProductFilterIds ?? [],
		ExcludedProductFilterIds: token.ExcludedProductFilterIds ?? [],
		CustomerGroupIds: token.CustomerGroupIds ?? [],
		img1: token.img1 ?? null,
		imgdel1: false,
		img1File: null,
		img2: token.img2 ?? null,
		imgdel2: false,
		img2File: null,
		img3: token.img3 ?? null,
		imgdel3: false,
		img3File: null,
		img4: token.img4 ?? null,
		imgdel4: false,
		img4File: null,
		img5: token.img5 ?? null,
		imgdel5: false,
		img5File: null
	};
}

export function formToToken(form: CategoryFormValues): CategoryToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		cd: form.cd,
		pst: form.pst,
		dsc: form.dsc,
		eurl: form.eurl,
		erpcd: form.erpcd,
		crf: form.crf,
		cri: form.cri,
		pid: form.pid,
		FilterTemplateId: form.FilterTemplateId,
		categoryWizardId: form.categoryWizardId,
		iv: form.iv,
		si: form.si,
		wt: form.wt,
		szq: form.szq,
		ilp: form.ilp,
		hidden_sbc: form.hidden_sbc,
		exclude_rel_category: form.exclude_rel_category,
		itp: form.itp,
		disable_indexation: form.disable_indexation,
		disable_follow: form.disable_follow,
		canonical_url: form.canonical_url,
		static_page_url: form.static_page_url?.trim() || null,
		gtax: form.gtax?.trim() || null,
		BuyGuideId: form.BuyGuideId,
		frontExclusion: form.frontExclusion,
		isHighlighted: form.isHighlighted,
		chex: form.chex,
		h1: form.h1,
		mt: form.mt,
		mk: form.mk,
		md: form.md,
		itxt1: form.itxt1,
		itxt2: form.itxt2,
		itxt3: form.itxt3,
		itxt4: form.itxt4,
		IsDynamic: form.IsDynamic,
		ProductFilterIds: form.ProductFilterIds,
		ExcludedProductFilterIds: form.ExcludedProductFilterIds,
		CustomerGroupIds: form.CustomerGroupIds,
		img1: form.img1,
		imgdel1: form.imgdel1,
		img2: form.img2,
		imgdel2: form.imgdel2,
		img3: form.img3,
		imgdel3: form.imgdel3,
		img4: form.img4,
		imgdel4: form.imgdel4,
		img5: form.img5,
		imgdel5: form.imgdel5
	};
}

/** Product-sort-type lookup item ({ id, nm }) returned by categories/getproductsorttypes. */
export interface ProductSortTypeOption {
	id: number;
	nm: string;
}

/** A node in the category tree built from the flat getAll list. */
export interface CategoryTreeNode extends CategoryToken {
	children: CategoryTreeNode[];
	depth: number;
}

/** All transitive descendant ids of `rootId` in the flat list (used to keep the parent select acyclic). */
export function collectDescendantIds(flat: CategoryToken[], rootId: number): Set<number> {
	const childrenOf = new Map<number, number[]>();
	for (const c of flat) {
		if (c.id == null || c.pid == null) continue;
		const arr = childrenOf.get(c.pid) ?? [];
		arr.push(c.id);
		childrenOf.set(c.pid, arr);
	}
	const out = new Set<number>();
	const stack = [rootId];
	while (stack.length) {
		const id = stack.pop()!;
		for (const child of childrenOf.get(id) ?? []) {
			if (!out.has(child)) {
				out.add(child);
				stack.push(child);
			}
		}
	}
	return out;
}

/**
 * Build a parent→child forest from the flat category list. Roots are nodes whose `pid`
 * is null/≤0 or points at an id not present in the set. Children are ordered by `si` then `nm`.
 */
export function buildCategoryTree(flat: CategoryToken[]): CategoryTreeNode[] {
	const byId = new Map<number, CategoryTreeNode>();
	for (const c of flat) {
		if (c.id != null) byId.set(c.id, { ...c, children: [], depth: 0 });
	}
	const roots: CategoryTreeNode[] = [];
	for (const node of byId.values()) {
		const parent = node.pid != null && node.pid > 0 ? byId.get(node.pid) : undefined;
		if (parent) parent.children.push(node);
		else roots.push(node);
	}
	const sortRec = (nodes: CategoryTreeNode[], depth: number) => {
		nodes.sort((a, b) => (a.si ?? 0) - (b.si ?? 0) || (a.nm ?? '').localeCompare(b.nm ?? ''));
		for (const n of nodes) {
			n.depth = depth;
			sortRec(n.children, depth + 1);
		}
	};
	sortRec(roots, 0);
	return roots;
}
